// Static *structure* for the Market Data page: which tabs exist, which periods
// are selectable, and which API field feeds each row.
//
// Nothing in this file is a market value. Every number shown on the page comes
// from the API at runtime - if a value is missing, the UI says so rather than
// substituting a plausible-looking constant.

export const MARKET_TABS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: '📊',
    description: 'All market factors',
  },
  {
    id: 'fedfunds',
    label: 'Fed Funds',
    icon: '🏛️',
    description: 'Federal funds rate and monetary policy',
    dataKey: 'fed_funds_rate',
    valueType: 'percent',
    accent: 'border-blue-500',
    fredSeries: 'FEDFUNDS',
    fredTitle: 'Federal Funds Rate',
  },
  {
    id: 'cpi',
    label: 'CPI Inflation',
    icon: '📈',
    description: 'Consumer price index and inflation trends',
    dataKey: 'cpi_inflation',
    valueType: 'percent',
    accent: 'border-red-500',
    fredSeries: 'CPIAUCSL',
    fredTitle: 'Consumer Price Index',
  },
  {
    id: 'unemployment',
    label: 'Unemployment',
    icon: '👥',
    description: 'Employment data and labor market conditions',
    dataKey: 'unemployment_rate',
    valueType: 'percent',
    accent: 'border-green-500',
    fredSeries: 'UNRATE',
    fredTitle: 'Unemployment Rate',
  },
  {
    id: 'treasury',
    label: '10Y Treasury',
    icon: '💰',
    description: '10-year treasury yields and bond market',
    dataKey: 'treasury_10y',
    valueType: 'percent',
    accent: 'border-yellow-500',
    fredSeries: 'DGS10',
    fredTitle: '10-Year Treasury Rate',
  },
  {
    id: 'mbs',
    label: 'MBS Spread',
    icon: '🏠',
    description: 'Mortgage-backed securities spread analysis',
    dataKey: 'mbs_spread',
    valueType: 'percent',
    accent: 'border-purple-500',
    fredSeries: 'MORTGAGE30US',
    fredTitle: '30-Year Mortgage Rate',
  },
  {
    id: 'qt',
    // valueType is 'decimal': QT pressure is a 0-1 index, not a rate. Rendering
    // it as a percent produced "0.60%", which reads as a rate at a glance.
    label: 'QT Pressure',
    icon: '⚖️',
    description: 'Quantitative tightening impact analysis',
    dataKey: 'qt_pressure',
    valueType: 'decimal',
    accent: 'border-indigo-500',
    fredSeries: 'WALCL',
    fredTitle: 'Fed Total Assets (QT Impact)',
  },
  {
    id: 'geopolitical',
    label: 'Geopolitical',
    icon: '🌍',
    description: 'Global events and risk factors',
    dataKey: 'geopolitical_risk',
    valueType: 'decimal',
    accent: 'border-orange-500',
    fredSeries: 'VIXCLS',
    fredTitle: 'VIX Volatility Index',
  },
];

export const getTab = (tabId) => MARKET_TABS.find((tab) => tab.id === tabId);

// `days` lives here so the period list and the API lookup can't drift apart -
// they were previously two separate maps that had to be kept in sync by hand.
export const PERIOD_OPTIONS = [
  { value: '1week', label: '1 Week', description: 'Very recent trends', icon: '⚡', days: 7 },
  { value: '1month', label: '1 Month', description: 'Short-term patterns', icon: '📊', days: 30 },
  { value: '3months', label: '3 Months', description: 'Quarterly trends', icon: '📈', days: 90 },
  { value: '6months', label: '6 Months', description: 'Seasonal patterns', icon: '🗓️', days: 180 },
  { value: '1year', label: '1 Year', description: 'Annual cycles', icon: '📅', days: 365 },
  { value: '2years', label: '2 Years', description: 'Policy cycles', icon: '🏛️', days: 730 },
  { value: '3years', label: '3 Years', description: 'Economic cycles', icon: '🌊', days: 1095 },
  { value: '4years', label: '4 Years', description: 'Presidential terms', icon: '🗳️', days: 1460 },
  { value: '5years', label: '5 Years', description: 'Long-term trends', icon: '📉', days: 1825 },
];

export const DEFAULT_PERIOD = '1month';

export const getPeriod = (value) =>
  PERIOD_OPTIONS.find((option) => option.value === value) || PERIOD_OPTIONS[1];

// The historical endpoint names a few fields differently from the current-values
// endpoint. Checked only after an exact key match fails.
export const HISTORICAL_KEY_ALIASES = {
  qt_intensity: ['qt_pressure'],
  inflation_rate: ['cpi_inflation'],
  core_pce: ['pce_inflation'],
  mortgage_rate_30y: ['mortgage_30yr', 'mortgage_30y'],
};

// The predictions endpoint has used shorter names for some rates than the
// current-values endpoint. Checked only after an exact key match fails.
export const PREDICTION_KEY_ALIASES = {
  mortgage_rate_30y: ['mortgage_rate'],
  inflation_rate: ['inflation'],
  cpi_inflation: ['inflation', 'inflation_rate'],
  unemployment_rate: ['unemployment'],
};

// Overview grid: which API fields to show, grouped, with the units to render them in.
export const INDICATOR_GROUPS = [
  {
    category: 'Federal Reserve Policy',
    icon: '🏛️',
    color: 'border-blue-500',
    items: [
      { label: 'Fed Funds Rate', key: 'fed_funds_rate', type: 'percent', description: 'Target federal funds rate set by the Federal Reserve' },
      { label: 'QT/QE Intensity', key: 'qt_intensity', type: 'decimal', description: 'Current quantitative policy stance (0=QE, 1=QT)' },
    ],
  },
  {
    category: 'Fed Balance Sheet (QT/QE Details)',
    icon: '📊',
    color: 'border-purple-500',
    items: [
      { label: 'Treasury Holdings', key: 'fed_treasury_holdings', type: 'billions', description: "Fed's Treasury securities portfolio (billions)" },
      { label: 'MBS Holdings', key: 'fed_mbs_holdings', type: 'billions', description: "Fed's mortgage-backed securities portfolio (billions)" },
      { label: 'Total Portfolio', key: 'fed_total_portfolio', type: 'billions', description: 'Combined Treasury + MBS holdings (billions)' },
      // Measured change over the last month, not the FOMC's runoff caps. The
      // caps had no data source and were hardcoded; these move with the actual
      // portfolio, and a positive number means it grew rather than ran off.
      { label: 'Treasury 1-Mo Change', key: 'treasury_holdings_change_1m', type: 'billions_month', description: 'Actual change in Treasury holdings over the last month (negative = runoff)' },
      { label: 'MBS 1-Mo Change', key: 'mbs_holdings_change_1m', type: 'billions_month', description: 'Actual change in MBS holdings over the last month (negative = runoff)' },
      { label: 'Total 1-Mo Change', key: 'total_holdings_change_1m', type: 'billions_month', description: 'Combined change in the portfolio over the last month (negative = runoff)' },
    ],
  },
  {
    category: 'Inflation Metrics',
    icon: '📈',
    color: 'border-red-500',
    items: [
      { label: 'Current CPI', key: 'inflation_rate', type: 'index', description: 'Consumer Price Index (base year index value)' },
      { label: '5Y Breakeven Inflation', key: 'inflation_expectations', type: 'percent', description: 'Market-based inflation expectations' },
      { label: 'Core PCE', key: 'core_pce', type: 'index', description: "Fed's preferred inflation measure (index)" },
    ],
  },
  {
    category: 'Employment',
    icon: '👥',
    color: 'border-green-500',
    items: [
      { label: 'Unemployment Rate', key: 'unemployment_rate', type: 'percent', description: 'Percentage of labor force unemployed' },
      { label: 'Job Openings', key: 'job_openings', type: 'thousands', description: 'Current job openings (thousands)' },
      { label: 'Weekly Claims', key: 'jobless_claims', type: 'thousands', description: 'Initial unemployment claims (thousands)' },
    ],
  },
  {
    category: 'Treasury & Bond Markets',
    icon: '💰',
    color: 'border-yellow-500',
    items: [
      { label: '10-Year Treasury', key: 'treasury_10y', type: 'percent', description: 'Benchmark 10-year Treasury yield' },
      { label: '2-Year Treasury', key: 'treasury_2y', type: 'percent', description: '2-year Treasury note yield' },
      { label: 'Yield Curve (10Y-2Y)', key: 'yield_curve_spread', type: 'percent', description: 'Spread between 10Y and 2Y Treasuries' },
    ],
  },
  {
    category: 'Mortgage Market',
    icon: '🏠',
    color: 'border-purple-500',
    items: [
      { label: '30Y Mortgage Rate', key: 'mortgage_rate_30y', type: 'percent', description: 'Average 30-year fixed mortgage rate' },
      { label: 'MBS Spread', key: 'mbs_spread', type: 'percent', description: 'Mortgage-backed securities spread over Treasury' },
      { label: 'MBS Demand Index', key: 'mbs_demand', type: 'decimal', description: 'Derived from the MBS spread: 0 = weak demand, 1 = strong' },
    ],
  },
  {
    category: 'Economic Indicators',
    icon: '📊',
    color: 'border-indigo-500',
    items: [
      { label: 'GDP Growth', key: 'gdp_growth', type: 'percent', description: 'Quarterly GDP growth rate (annualized)' },
      { label: 'Consumer Confidence', key: 'consumer_confidence', type: 'index', description: 'Consumer confidence index' },
      { label: 'Manufacturing Employment', key: 'pmi_manufacturing', type: 'thousands', description: 'All employees, manufacturing sector (thousands)' },
    ],
  },
];

// Provenance for the footer. Describes *where* data comes from; contains no values.
export const DATA_SOURCES = [
  {
    name: 'Federal Reserve (FRED)',
    series: ['Fed Funds Rate', 'Fed Balance Sheet', 'Inflation Data', 'Employment Data', 'GDP Growth'],
  },
  {
    name: 'Treasury Department',
    series: ['Treasury Yields', 'Yield Curve Data', 'Auction Results'],
  },
  {
    name: 'Market Data Providers',
    series: ['Mortgage Rates', 'MBS Pricing', 'Economic Indicators'],
  },
];
